import { useCallback, useEffect, useState } from "react";
import { Facebook, Instagram, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { api } from "../lib/apiClient";

const COMMON_EMAIL = "lecomptoirducookie@gmail.com";
const INSTAGRAM_URL = "https://www.instagram.com/lecomptoirducookie/";
const FACEBOOK_URL = "https://www.facebook.com/lecomptoirducookie/";

export default function Contact() {
    const [shops, setShops] = useState([]);

    const fetchShops = useCallback(async () => {
        try {
            const { data } = await api.get("/shops");
            setShops(data || []);
        } catch (_e) {
            // silent
        }
    }, []);

    useEffect(() => {
        fetchShops();
    }, [fetchShops]);

    return (
        <div data-testid="contact-page">
            {/* HEADER */}
            <section className="bg-[#FFF1E0] border-b-2 border-[#2A1408]">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 py-20 text-center relative">
                    <span
                        className="hidden md:inline-block absolute top-24 right-10 sticker"
                        style={{ transform: "rotate(7deg)" }}
                    >
                        on vous lit
                    </span>
                    <p className="section-tag">on parle ?</p>
                    <h1 className="font-display text-7xl md:text-9xl text-[#2A1408] leading-[0.85] mt-2">
                        Nous contacter.
                    </h1>
                    <p className="mt-5 text-[#5C3D2E] max-w-2xl mx-auto text-lg">
                        Question, commande sur-mesure, évènement ? On vous répond avec
                        plaisir, et toujours rapidement.
                    </p>
                </div>
            </section>

            {/* SHOPS PHONES + ADDRESSES */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 py-20">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
                    <div>
                        <p className="section-tag">par téléphone</p>
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408] leading-[0.9]">
                            Nos boutiques.
                        </h2>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    {shops.map((s, i) => (
                        <article
                            key={s.slug}
                            className={`brand-card p-7 ${i % 2 === 0 ? "tilt-l" : "tilt-r"} hover:!rotate-0`}
                            data-testid={`contact-shop-${s.slug}`}
                        >
                            <p className="text-xs uppercase tracking-widest text-[#B07B66] font-bold">
                                Boutique
                            </p>
                            <h3 className="font-display text-3xl text-[#2A1408] leading-none mt-1">
                                {s.name}
                            </h3>
                            <p className="mt-4 flex items-start gap-2 text-[#3D1F0F]">
                                <MapPin size={18} strokeWidth={2.2} className="mt-0.5 shrink-0" />
                                <span>{s.address}</span>
                            </p>
                            {s.phone && (
                                <a
                                    href={`tel:${s.phone.replace(/\s+/g, "")}`}
                                    className="mt-3 flex items-center gap-2 text-[#3D1F0F] hover:text-[#D6552C] font-display text-2xl"
                                    data-testid={`contact-phone-${s.slug}`}
                                >
                                    <Phone size={20} strokeWidth={2.2} /> {s.phone}
                                </a>
                            )}
                        </article>
                    ))}
                </div>
            </section>

            {/* EMAIL + SOCIAL */}
            <section className="bg-[#F5E6D3] border-t-2 border-[#2A1408]">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 grid md:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="font-handwritten text-3xl text-[#B07B66]">
                            par email
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408] leading-[0.9]">
                            On vous écrit ?
                        </h2>
                        <p className="mt-4 text-[#3D1F0F] max-w-md">
                            Pour toute demande commerciale, partenariat ou
                            information, écrivez-nous directement.
                        </p>
                        <a
                            href={`mailto:${COMMON_EMAIL}`}
                            className="brand-btn brand-btn-pop mt-8"
                            data-testid="contact-email-btn"
                        >
                            <Mail size={18} /> {COMMON_EMAIL}
                        </a>
                    </div>

                    <div>
                        <p className="font-handwritten text-3xl text-[#B07B66]">
                            sur les réseaux
                        </p>
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408] leading-[0.9]">
                            DM &amp; SMS.
                        </h2>
                        <p className="mt-4 text-[#3D1F0F] max-w-md">
                            Le plus rapide&nbsp;: on répond en DM sur Instagram, Facebook,
                            ou directement par SMS aux numéros ci-dessus. Pas de
                            messagerie auto, pas de bot, juste nous.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-4">
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#2A1408] text-[#2A1408] hover:bg-[#2A1408] hover:text-[#FFF1E0] transition-colors font-bold"
                                data-testid="contact-instagram"
                            >
                                <Instagram size={18} strokeWidth={2.2} /> Instagram
                            </a>
                            <a
                                href={FACEBOOK_URL}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#2A1408] text-[#2A1408] hover:bg-[#2A1408] hover:text-[#FFF1E0] transition-colors font-bold"
                                data-testid="contact-facebook"
                            >
                                <Facebook size={18} strokeWidth={2.2} /> Facebook
                            </a>
                            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#D6552C] text-[#FFF1E0] font-bold">
                                <MessageCircle size={18} strokeWidth={2.2} /> SMS bienvenu
                            </span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
